import { action, createAsyncAction } from 'typesafe-actions'
import { Dispatch } from 'redux'
import api from 'api/graphql/graphql'
import { GqlErrors, MY_SOCIAL_MATCHES_KEY, Paging, SocialMatchUpUI } from 'types/global'
import { MatchUpSubmissionStateEnum } from 'types/api/enums'
import { toast } from 'react-toastify'
import i18n from '../i18n'
import { SelfSubmittedMatchUpInput } from 'types/api/types'
import { ApolloError } from 'apollo-client'

export const CLEAR_LAST_SUBMITTED = 'socialMatches/CLEAR_LAST_SUBMITTED'
export const RESET_SOCIAL_MATCHES = 'socialMatches/RESET_SOCIAL_MATCHES'

export const SOCIAL_MATCHES_REQUESTED = 'socialMatches/SOCIAL_MATCHES_REQUESTED'
export const SOCIAL_MATCHES_COMPLETED = 'socialMatches/SOCIAL_MATCHES_COMPLETED'
export const SOCIAL_MATCHES_FAILED = 'socialMatches/SOCIAL_MATCHES_FAILED'
export const requestSocialMatches = createAsyncAction(
  SOCIAL_MATCHES_REQUESTED,
  SOCIAL_MATCHES_COMPLETED,
  SOCIAL_MATCHES_FAILED
)<
  MatchUpSubmissionStateEnum,
  {
    playerKey: string
    matchSubmissionState: MatchUpSubmissionStateEnum
    paginatedMatches: SocialMatchUpUI[]
    recentlyUpdatedMatches: SocialMatchUpUI[]
    totals: { [key in MatchUpSubmissionStateEnum]: number }
  },
  MatchUpSubmissionStateEnum
>()

export const UPDATE_SOCIAL_MATCH_REQUESTED = 'socialMatches/UPDATE_SOCIAL_MATCH_REQUESTED'
export const UPDATE_SOCIAL_MATCH_COMPLETED = 'socialMatches/UPDATE_SOCIAL_MATCH_COMPLETED'
export const UPDATE_SOCIAL_MATCH_FAILED = 'socialMatches/UPDATE_SOCIAL_MATCH_FAILED'
export const updateSocialMatch = createAsyncAction(
  UPDATE_SOCIAL_MATCH_REQUESTED,
  UPDATE_SOCIAL_MATCH_COMPLETED,
  UPDATE_SOCIAL_MATCH_FAILED
)<string, SocialMatchUpUI, { matchId: string; shouldRemoveMatch: boolean }>()

export const SUBMIT_SOCIAL_MATCH_REQUESTED = 'socialMatches/SUBMIT_SOCIAL_MATCH_REQUESTED'
export const SUBMIT_SOCIAL_MATCH_COMPLETED = 'socialMatches/SUBMIT_SOCIAL_MATCH_COMPLETED'
export const SUBMIT_SOCIAL_MATCH_FAILED = 'socialMatches/SUBMIT_SOCIAL_MATCH_FAILED'
export const submitSocialMatch = createAsyncAction(
  SUBMIT_SOCIAL_MATCH_REQUESTED,
  SUBMIT_SOCIAL_MATCH_COMPLETED,
  SUBMIT_SOCIAL_MATCH_FAILED
)<void, SocialMatchUpUI, Error>()

export const DELETE_SOCIAL_MATCH_REQUESTED = 'socialMatches/DELETE_SOCIAL_MATCH_REQUESTED'
export const DELETE_SOCIAL_MATCH_COMPLETED = 'socialMatches/DELETE_SOCIAL_MATCH_COMPLETED'
export const DELETE_SOCIAL_MATCH_FAILED = 'socialMatches/DELETE_SOCIAL_MATCH_FAILED'
export const deleteSocialMatch = createAsyncAction(
  DELETE_SOCIAL_MATCH_REQUESTED,
  DELETE_SOCIAL_MATCH_COMPLETED,
  DELETE_SOCIAL_MATCH_FAILED
)<string, string, string>()

export const clearLastSubmittedSocialMatch = () => action(CLEAR_LAST_SUBMITTED)

export const resetSocialMatches = (state: MatchUpSubmissionStateEnum) =>
  action(RESET_SOCIAL_MATCHES, state)

export const getMySocialMatches = (state: MatchUpSubmissionStateEnum, paging: Paging) => {
  return (dispatch: Dispatch) => {
    dispatch(requestSocialMatches.request(state))
    api
      .getOwnSocialMatches(state, paging)
      .then(data => {
        dispatch(
          requestSocialMatches.success({
            playerKey: MY_SOCIAL_MATCHES_KEY,
            matchSubmissionState: state,
            ...data,
          })
        )
      })
      .catch(e => {
        console.warn('Failed to fetch social matches', e && e.message)
        dispatch(requestSocialMatches.failure(state))
      })
  }
}

export const getApprovedMatchesForPlayer = (playerId: string, paging: Paging) => {
  return (dispatch: Dispatch) => {
    dispatch(requestSocialMatches.request(MatchUpSubmissionStateEnum.APPROVED))
    api
      .getApprovedMatches(playerId, paging)
      .then(data => {
        dispatch(
          requestSocialMatches.success({
            playerKey: playerId,
            matchSubmissionState: MatchUpSubmissionStateEnum.APPROVED,
            ...data,
          })
        )
      })
      .catch(e => {
        console.warn('Failed to fetch social matches', e && e.message)
        dispatch(requestSocialMatches.failure(MatchUpSubmissionStateEnum.APPROVED))
      })
  }
}

export const approvePending = (id: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(updateSocialMatch.request(id))
    try {
      dispatch(
        updateSocialMatch.success(
          await toast.promise(api.approveSocialMatch(id), {
            pending: i18n.t('approvingSocialMatch', { ns: 'strAlerts' }),
            success: i18n.t('socialMatchApproved', { ns: 'strAlerts' }),
          })
        )
      )
    } catch (e) {
      const { graphQLErrors, message } = e as ApolloError
      console.warn('Failed to approve social match', message)

      const itemHasBeenDeleted =
        graphQLErrors?.some(err => err?.extensions?.code === GqlErrors.ITEM_DELETED) ?? false

      const toastType = itemHasBeenDeleted ? 'warn' : 'error'
      const messageKey = itemHasBeenDeleted
        ? 'socialMatchAlreadyDeleted'
        : 'errorApprovingSocialMatch'

      toast[toastType](i18n.t(messageKey, { ns: 'strAlerts' }))

      dispatch(
        updateSocialMatch.failure({
          matchId: id,
          shouldRemoveMatch: itemHasBeenDeleted,
        })
      )
    }
  }
}

export const rejectPending = (id: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(updateSocialMatch.request(id))
    try {
      dispatch(
        updateSocialMatch.success(
          await toast.promise(api.rejectSocialMatch(id), {
            pending: i18n.t('rejectingSocialMatch', { ns: 'strAlerts' }),
            success: i18n.t('socialMatchRejected', { ns: 'strAlerts' }),
          })
        )
      )
    } catch (e) {
      const { graphQLErrors, message } = e as ApolloError
      console.warn('Failed to reject social match', message)

      const itemHasBeenDeleted =
        graphQLErrors?.some(err => err?.extensions?.code === GqlErrors.ITEM_DELETED) ?? false

      const toastType = itemHasBeenDeleted ? 'warn' : 'error'
      const messageKey = itemHasBeenDeleted
        ? 'socialMatchAlreadyDeleted'
        : 'errorRejectingSocialMatch'

      toast[toastType](i18n.t(messageKey, { ns: 'strAlerts' }))

      dispatch(
        updateSocialMatch.failure({
          matchId: id,
          shouldRemoveMatch: itemHasBeenDeleted,
        })
      )
    }
  }
}

export const deletePending = (id: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(deleteSocialMatch.request(id))
    try {
      dispatch(
        deleteSocialMatch.success(
          await toast.promise(api.deleteSocialMatch(id), {
            pending: i18n.t('deletingSocialMatch', { ns: 'strAlerts' }),
            success: i18n.t('socialMatchDeleted', { ns: 'strAlerts' }),
          })
        )
      )
    } catch (e) {
      const { graphQLErrors, message } = e as ApolloError
      console.warn('Failed to delete social match', message)

      const wasAlreadyDeleted =
        graphQLErrors?.some(err => err?.extensions?.code === GqlErrors.ITEM_DELETED) ?? false
      const wasAlreadyApproved =
        graphQLErrors?.some(err => err?.extensions?.code === GqlErrors.ITEM_APPROVED) ?? false

      let toastType: 'success' | 'warn' | 'error' = 'error'
      let messageKey = 'errorDeletingSocialMatch'

      if (wasAlreadyDeleted) {
        toastType = 'success'
        messageKey = 'socialMatchDeleted'
        dispatch(deleteSocialMatch.success(id))
      } else if (wasAlreadyApproved) {
        toastType = 'warn'
        messageKey = 'socialMatchAlreadyApproved'
        dispatch(deleteSocialMatch.failure(id))
      } else {
        dispatch(deleteSocialMatch.failure(id))
      }

      toast[toastType](i18n.t(messageKey, { ns: 'strAlerts' }))
    }
  }
}

export const submitSocialMatchUp = (input: SelfSubmittedMatchUpInput) => {
  return async (dispatch: Dispatch) => {
    dispatch(submitSocialMatch.request())
    try {
      dispatch(submitSocialMatch.success(await api.submitSocialMatch(input)))
    } catch (e) {
      const err = e as Error
      console.warn('Failed to submit social match', err && err.message)
      dispatch(submitSocialMatch.failure(err))
      toast.error(i18n.t('errorSubmittingSocialMatch', { ns: 'strAlerts' }))
    }
  }
}
